/**
 * Query parameters understood by the app. Names and values are kept identical
 * to the legacy app so that existing bookmarks and shared links keep working.
 */

export type Tab = "list" | "matrix" | "stats" | "system" | "admin";
export type DetailsPosition = "right" | "below";

const PAGE_BY_TAB: Record<Tab, string> = {
  list: "1",
  matrix: "2",
  stats: "3",
  system: "4",
  admin: "5",
};
const TAB_BY_PAGE = Object.fromEntries(
  Object.entries(PAGE_BY_TAB).map(([tab, page]) => [page, tab as Tab]),
);

export interface InitialUrlState {
  tab: Tab | null;
  viewId: string | null;
  language: string | null;
  /** Requested location names (case-insensitive, matched once data loads). */
  locations: string[];
  showTitles: boolean | null;
  showIds: boolean | null;
  detailsPosition: DetailsPosition | null;
  detailsWidth: number | null;
  columnLayout: string | null;
  dataFile: string | null;
}

function parseBoolean(value: string | null): boolean | null {
  const normalized = value?.trim().toLowerCase();
  return normalized === "true" ? true : normalized === "false" ? false : null;
}

function parseDetailsPosition(value: string | null): DetailsPosition | null {
  const normalized = value?.trim().toLowerCase();
  return normalized === "right" || normalized === "below" ? normalized : null;
}

export function readUrlState(search: string = window.location.search): InitialUrlState {
  const params = new URLSearchParams(search);
  const width = Number(params.get("detailsWidth"));
  // `useTiltaksklasse` is the legacy spelling of `columnLayout`.
  const legacyLayout = parseBoolean(params.get("useTiltaksklasse"));
  return {
    tab: TAB_BY_PAGE[params.get("page") ?? ""] ?? null,
    viewId: params.get("view"),
    language: params.get("lang"),
    locations: (params.get("location") ?? "")
      .split(",")
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean),
    showTitles: parseBoolean(params.get("itemTitles")),
    showIds: parseBoolean(params.get("itemIds")),
    detailsPosition: parseDetailsPosition(
      params.get("detailsPosition") ?? params.get("detailsLayout"),
    ),
    detailsWidth: Number.isFinite(width) && width > 0 ? width : null,
    columnLayout:
      legacyLayout === null
        ? params.get("columnLayout")
        : legacyLayout
          ? "tiltaksklasse"
          : "ageGroups",
    dataFile: params.get("data"),
  };
}

export interface UrlWritableState {
  tab: Tab;
  viewId: string;
  language: string;
  showTitles: boolean;
  showIds: boolean;
  detailsPosition: DetailsPosition;
  detailsWidth: number;
}

/** Mirrors the shareable state into the address bar without adding history entries. */
export function writeUrlState(state: UrlWritableState): void {
  const url = new URL(window.location.href);
  url.searchParams.set("page", PAGE_BY_TAB[state.tab]);
  url.searchParams.set("view", state.viewId);
  url.searchParams.set("lang", state.language);
  url.searchParams.set("itemTitles", String(state.showTitles));
  url.searchParams.set("itemIds", String(state.showIds));
  url.searchParams.set("detailsPosition", state.detailsPosition);
  url.searchParams.set("detailsWidth", String(Math.round(state.detailsWidth)));
  if (url.href !== window.location.href) window.history.replaceState(window.history.state, "", url);
}

const LANGUAGE_STORAGE_KEY = "tiltak_lang";

function readStoredLanguage(): string | null {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeLanguage(language: string): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Storage may be unavailable (private mode); the URL still carries the language.
  }
}

export function detectInitialLanguage(urlLanguage: string | null): string {
  return (
    urlLanguage || readStoredLanguage() || (navigator.language?.startsWith("en") ? "en" : "no")
  );
}
